<script setup lang="ts">
import { SHOW_SCAN_TICKET, SHOW_TAB_SCAN_TICKET_ALWAYS } from '~/config/featureFlags'
import { useAuthStore } from '~/store/auth'
import { trackBannerScanClick } from '~/tracking/scan/scanSection'

defineOptions({
	name: 'ScanTicketBanner',
})

const DISMISSED_COOKIE_KEY = 'results_scan_ticket_banner_dismissed'
const route = useRoute()

const { $hasFeature: _$hasFeature } = useNuxtApp()
const $hasFeature = _$hasFeature ?? (() => false)
const localePath = useLocalePath()
const authStore = useAuthStore()
const { loggedIn } = storeToRefs(authStore)
const { t: $t } = useI18n()

const dismissedCookie = useCookie<boolean | null>(DISMISSED_COOKIE_KEY, {
	default: () => null,
	maxAge: 60 * 60 * 24 * 365,
	path: '/',
	sameSite: 'lax',
})

const isVisible = computed(() => {
	return $hasFeature(SHOW_SCAN_TICKET) && dismissedCookie.value !== true
})

const getLogin = computed(() => loggedIn.value)

const openScanTicket = async () => {
	if ($hasFeature(SHOW_TAB_SCAN_TICKET_ALWAYS) === true) {
		trackBannerScanClick({
			destination: 'scan_ticket',
		})
		const basePath = localePath('/results/scan')
		await navigateTo({
			path: basePath,
			query: route.query,
			replace: true,
		})
		return
	}
	if (getLogin.value === false) {
		authStore.SET_STATE_AUTH({
			type: 'showModalSignUp',
			value: true,
		})
		trackBannerScanClick({
			destination: 'signup',
		})
		return
	}
	trackBannerScanClick({
		destination: 'scan_ticket',
	})
	const basePath = localePath('/results/scan')
	await navigateTo({
		path: basePath,
		query: route.query,
		replace: true,
	})
}

const dismissBanner = () => {
	dismissedCookie.value = true
}

const onBannerKeydown = async (event: KeyboardEvent) => {
	if (event.key !== 'Enter' && event.key !== ' ') return
	event.preventDefault()
	await openScanTicket()
}
</script>

<template>
	<article
		v-if="isVisible"
		class="group relative overflow-hidden rounded-2xl border border-primary-500/20 bg-[linear-gradient(145deg,rgba(34,221,90,0.15)_0%,#1C1A23_100%)] px-4 py-4 transition-colors duration-200 hover:border-primary-500/35 focus-within:border-primary-500/35 sm:px-[17px] sm:py-[17px]"
		role="button"
		tabindex="0"
		:aria-label="$t('Open scan ticket')"
		@click="openScanTicket"
		@keydown="onBannerKeydown"
	>
		<div class="flex items-start gap-4 sm:items-center">
			<div
				class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-500/20 text-primary-500"
				aria-hidden="true"
			>
				<UIcon
					name="i-mdi:line-scan"
					class="size-5"
				/>
			</div>

			<div class="min-w-0 flex-1 space-y-0.5 sm:space-y-0">
				<div class="sm:hidden">
					<h2 class="text-sm font-bold leading-5 text-neutral-100">
						{{ $t('Scan your ticket now!') }}
					</h2>
					<p class="text-xs leading-4 text-neutral-300">
						{{ $t('Scan your tickets instantly with our OCR-powered scanner. Try it now!') }}
					</p>
				</div>

				<div class="hidden sm:block">
					<h2 class="text-sm font-bold leading-5 text-neutral-100">
						{{ $t('Check your ticket now!') }}
					</h2>
					<p class="text-xs leading-4 text-neutral-200">
						{{ $t('Upload a photo or enter your numbers to instantly see if you won') }}
					</p>
				</div>
			</div>

			<button
				type="button"
				class="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-[#2B2932]/50 text-neutral-600 transition-colors duration-200 hover:bg-[#2B2932] hover:text-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 sm:mt-0"
				:aria-label="$t('Close scan ticket banner')"
				@click.stop="dismissBanner"
				@keydown.stop
			>
				<UIcon
					name="i-heroicons-x-mark-20-solid"
					class="size-4"
				/>
			</button>
		</div>
	</article>
</template>
